import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useProgress,
  Html,
  Stage,
  AdaptiveDpr,
  PerformanceMonitor,
} from "@react-three/drei";
import Model from "./Model";

const MODEL_TIMEOUT_MS = 28000;

const Building3D = ({
  name,
  siteId,
  style,
  isCompact = false,
  isFullscreen = false,
  onToggleFullscreen,
  drops = [],
  showStatusColors = true,
  lowPerformanceMode = false,
  onToggleLowPerformance,
  showLegend = true,
  onToggleLegend,
  onToggle3D,
  isLikelyLowEndDevice = false,
}) => {
  const [modelError, setModelError] = useState("");
  const [degraded, setDegraded] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [webglCanvas, setWebglCanvas] = useState(null);

  const resolvedSiteId = useMemo(() => siteId || name || "", [name, siteId]);

  useEffect(() => {
    setModelError("");
  }, [resolvedSiteId, lowPerformanceMode]);

  const handleRetry = useCallback(() => {
    setModelError("");
    setRenderKey((prev) => prev + 1);
  }, []);

  const handleOpenLowQuality = useCallback(() => {
    onToggleLowPerformance?.(true);
    setModelError("");
    setRenderKey((prev) => prev + 1);
  }, [onToggleLowPerformance]);

  const onModelError = useCallback((error) => {
    const message =
      error?.message ||
      "3D view could not be loaded. You can retry or switch to Low Performance Mode.";
    setModelError(message);
  }, []);

  const onModelTimeout = useCallback(() => {
    setModelError("This model is taking too long to open on this phone.");
  }, []);

  const onWebGLContextLost = useCallback(() => {
    setModelError(
      "The phone stopped the 3D view to protect memory. Retry or use Low Performance Mode."
    );
  }, []);

  useEffect(() => {
    if (!webglCanvas || !webglCanvas.addEventListener) {
      return undefined;
    }

    const onContextLost = (event) => {
      event.preventDefault?.();
      onWebGLContextLost();
    };

    webglCanvas.addEventListener("webglcontextlost", onContextLost, false);
    webglCanvas.addEventListener("webglcontextrestored", handleRetry, false);

    return () => {
      webglCanvas.removeEventListener("webglcontextlost", onContextLost, false);
      webglCanvas.removeEventListener("webglcontextrestored", handleRetry, false);
    };
  }, [webglCanvas, onWebGLContextLost, handleRetry]);

  if (!resolvedSiteId) {
    return (
      <View style={[styles.stateCard, style]}>
        <Text style={styles.stateTitle}>No 3D model selected</Text>
        <Text style={styles.stateText}>
          Choose a building to display the 3D viewer.
        </Text>
      </View>
    );
  }

  if (modelError) {
    return (
      <View style={[styles.stateCard, style]}>
        <Text style={styles.stateTitle}>3D Viewer needs help</Text>
        <Text style={styles.stateText}>{modelError}</Text>
        <View style={styles.errorButtonRow}>
          <Pressable onPress={handleRetry} style={styles.recoveryButton}>
            <Text style={styles.recoveryButtonText}>Retry</Text>
          </Pressable>
          <Pressable
            onPress={handleOpenLowQuality}
            style={[styles.recoveryButton, styles.recoveryLowButton]}
          >
            <Text style={styles.recoveryButtonText}>Open in Low Quality</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.canvasWrapper, isFullscreen && styles.canvasWrapperFullscreen, style]}>

      {!isFullscreen && isLikelyLowEndDevice ? (
        <View style={styles.phoneWarningCard}>
          <Text style={styles.phoneWarningText}>
            3D may be slow on your phone. Low Performance Mode is recommended.
          </Text>
        </View>
      ) : null}

      {showLegend ? <StatusLegend compact={isFullscreen} /> : null}

      <Canvas
        key={renderKey}
        frameloop="demand"
        dpr={
          lowPerformanceMode
            ? [1, 1.1]
            : degraded
              ? [1, isFullscreen ? 1.3 : 1.2]
              : [1, isFullscreen ? 1.7 : 1.5]
        }
        camera={{
          fov: isFullscreen ? 50 : isCompact ? 48 : 42,
          position: [0, 2.2, isCompact ? 8.5 : 9.5],
        }}
        shadows={!lowPerformanceMode}
        gl={{
          antialias: !lowPerformanceMode,
          powerPreference: lowPerformanceMode ? "low-power" : "high-performance",
          alpha: false,
        }}
        style={styles.canvasElement}
        onCreated={({ gl }) => {
          const canvas = gl?.domElement;
          setWebglCanvas(canvas || null);
        }}
      >
        <color attach="background" args={[lowPerformanceMode ? "#f1f5f9" : "#f8fbff"]} />

        <PerformanceMonitor
          onDecline={() => setDegraded(true)}
          onIncline={() => setDegraded(false)}
        />

        <Suspense fallback={<Loader isLowPerformance={lowPerformanceMode} />}>
          <LoadingWatchdog timeoutMs={MODEL_TIMEOUT_MS} onTimeout={onModelTimeout} />
          <Stage
            adjustCamera={1.1}
            intensity={lowPerformanceMode ? 0.45 : 0.65}
            shadows={!lowPerformanceMode}
            environment={lowPerformanceMode ? undefined : "city"}
          >
            <Model
              siteId={resolvedSiteId}
              onError={onModelError}
              drops={drops}
              showStatusColors={showStatusColors}
              lowPerformanceMode={lowPerformanceMode}
            />
          </Stage>
          <AdaptiveDpr pixelated />
        </Suspense>

        <OrbitControls
          makeDefault
          enablePan={isFullscreen}
          enableDamping
          dampingFactor={0.1}
          rotateSpeed={isFullscreen ? 1.05 : isCompact ? 0.72 : 0.88}
          zoomSpeed={isFullscreen ? 1.05 : 0.9}
          minDistance={isCompact ? 4 : 5}
          maxDistance={lowPerformanceMode ? 12 : isFullscreen ? 16 : isCompact ? 10 : 14}
        />
      </Canvas>
    </View>
  );
};

function LoadingWatchdog({ timeoutMs, onTimeout }) {
  const { active, progress } = useProgress();

  useEffect(() => {
    if (!active || progress >= 100) {
      return undefined;
    }

    const timer = setTimeout(() => {
      onTimeout?.();
    }, timeoutMs);

    return () => {
      clearTimeout(timer);
    };
  }, [active, progress, timeoutMs, onTimeout]);

  return null;
}

function Loader({ isLowPerformance = false }) {
  const { active, progress } = useProgress();
  const [secondsElapsed, setSecondsElapsed] = useState(0);

  useEffect(() => {
    if (!active) {
      setSecondsElapsed(0);
      return undefined;
    }

    const timer = setInterval(() => {
      setSecondsElapsed((prev) => prev + 1);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [active]);

  const estimatedRemaining = useMemo(() => {
    if (progress <= 5 || secondsElapsed <= 0) {
      return null;
    }
    const totalEstimate = secondsElapsed / (progress / 100);
    return Math.max(1, Math.round(totalEstimate - secondsElapsed));
  }, [progress, secondsElapsed]);

  return (
    <Html center>
      <div style={loaderStyles.wrapper}>
        <div style={loaderStyles.title}>Opening 3D model...</div>
        <div style={loaderStyles.barTrack}>
          <div style={{ ...loaderStyles.barFill, width: `${Math.min(progress, 100)}%` }} />
        </div>
        <div style={loaderStyles.label}>{Math.round(progress)}% loaded</div>
        <div style={loaderStyles.hint}>
          {estimatedRemaining
            ? `This may take a moment on your phone (~${estimatedRemaining}s left)`
            : "This may take a moment on your phone"}
        </div>
        {isLowPerformance ? (
          <div style={loaderStyles.lowModeTag}>Low Performance Mode enabled</div>
        ) : null}
      </div>
    </Html>
  );
}

function StatusLegend({ compact = false }) {
  return (
    <View style={[styles.legendCard, compact && styles.legendCardCompact]} pointerEvents="none">
      <LegendItem color="#16a34a" label="Cleaned" />
      <LegendItem color="#dc2626" label="Faulty" />
      <LegendItem color="#0ea5e9" label="Reach & Wash" />
    </View>
  );
}

function LegendItem({ color, label }) {
  return (
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: color }]} />
      <Text style={styles.legendLabel}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  canvasWrapper: {
    flex: 1,
    width: "100%",
    minHeight: 290,
  },
  canvasWrapperFullscreen: {
    minHeight: "100vh",
    height: "100%",
    backgroundColor: "#000000",
  },
  canvasElement: {
    flex: 1,
    width: "100%",
    height: "100%",
    touchAction: "none",
  },
  phoneWarningCard: {
    position: "absolute",
    top: 12,
    left: 12,
    right: 12,
    zIndex: 15,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 14,
    backgroundColor: "rgba(30, 41, 59, 0.88)",
  },
  phoneWarningText: {
    color: "#f8fafc",
    fontSize: 12,
    lineHeight: 18,
    textAlign: "center",
    fontWeight: "600",
  },
  legendCard: {
    position: "absolute",
    left: 12,
    bottom: 12,
    zIndex: 14,
    borderRadius: 14,
    backgroundColor: "rgba(15, 23, 42, 0.72)",
    paddingVertical: 9,
    paddingHorizontal: 10,
  },
  legendCardCompact: {
    bottom: 20,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 2,
  },
  legendDot: {
    width: 9,
    height: 9,
    borderRadius: 999,
    marginRight: 8,
  },
  legendLabel: {
    color: "#f8fafc",
    fontSize: 11,
    fontWeight: "600",
  },
  stateCard: {
    flex: 1,
    width: "100%",
    minHeight: 290,
    paddingHorizontal: 20,
    paddingVertical: 18,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafc",
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  stateTitle: {
    color: "#111827",
    fontSize: 17,
    fontWeight: "800",
    marginBottom: 8,
    textAlign: "center",
  },
  stateText: {
    color: "#4b5563",
    fontSize: 14,
    lineHeight: 20,
    textAlign: "center",
  },
  errorButtonRow: {
    marginTop: 14,
    width: "100%",
    gap: 10,
  },
  recoveryButton: {
    minHeight: 48,
    borderRadius: 12,
    backgroundColor: "#0f766e",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 14,
  },
  recoveryLowButton: {
    backgroundColor: "#a16207",
  },
  recoveryButtonText: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: "800",
  },
});

const loaderStyles = {
  wrapper: {
    minWidth: 220,
    borderRadius: 14,
    padding: "12px 13px",
    background: "rgba(17, 24, 39, 0.9)",
    color: "#f9fafb",
  },
  title: {
    fontSize: "12px",
    fontWeight: "700",
    marginBottom: "7px",
  },
  barTrack: {
    width: "100%",
    height: "8px",
    borderRadius: "999px",
    background: "rgba(255, 255, 255, 0.22)",
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: "999px",
    background: "#38bdf8",
    transition: "width 120ms ease-out",
  },
  label: {
    marginTop: "8px",
    fontSize: "12px",
    textAlign: "center",
    letterSpacing: "0.2px",
  },
  hint: {
    marginTop: "6px",
    fontSize: "11px",
    lineHeight: "14px",
    textAlign: "center",
    color: "#cbd5e1",
  },
  lowModeTag: {
    marginTop: "8px",
    fontSize: "11px",
    fontWeight: "700",
    textAlign: "center",
    color: "#fef3c7",
  },
};

export default React.memo(Building3D);
